import React, { useEffect } from 'react'
import { store } from '@redux/store'
import { Provider } from 'react-redux'
import Modules from '@components/Modules'
import { graphql, navigate, useStaticQuery } from 'gatsby'
import { useLocation } from '@reach/router'
import { GlobalDataProvider } from '@context/GlobalDataContext'
import fontMap from './generated/font-map.json'

export const wrapRootElement = ({ element }: any) => {
  return <Provider store={store}>{element}</Provider>
}

const NavigationHandler = () => {
  const location = useLocation()
  const data = useStaticQuery(graphql`
    query GlobalStaticData {
      siteCompilerData {
        prehome_components {
          type_id
        }
        divisions {
          path
        }
      }
    }
  `)

  const isSiteCompiler = process.env.GATSBY_IS_SITE_COMPILER === 'true'
  const useHomeFirstDivision =
    isSiteCompiler && data?.siteCompilerData?.prehome_components?.length === 0
  const firstDivisionPath = isSiteCompiler && data?.siteCompilerData?.divisions[0]?.path

  useEffect(() => {
    if (location.pathname === '/' && useHomeFirstDivision && firstDivisionPath) {
      navigate(firstDivisionPath)
    }
  }, [useHomeFirstDivision, firstDivisionPath, location.pathname])

  return null
}
export const wrapPageElement = ({ element, props }: any) => {
  // Si la página indica que es una redirección/placeholder, devolvemos solo el elemento
  // sin intentar leer global_data, header, footer, fuentes, etc.
  const safeProps = props || {}
  const pageContext = safeProps.pageContext || {}
  const isRedirection = pageContext.is_redirection ?? safeProps.is_redirection ?? false
  if (isRedirection) {
    console.log('Redirection page detected, rendering without global layout.')
    console.log(element)
    return <div suppressHydrationWarning>{element}</div>
  }

  const {
    pageContext: { global_data, hidden_header, hidden_footer, divisions, three },
    development_id,
    property_id,
    novelty_id,
  } = props

  const {
    content: { header, pages, footer },
    pageConfig: {
      design: { h1, h2, h3, h4, h5, p, p_bold, a, global },
    },
  } = global_data

  let Header
  try {
    Header = require(`./components/header/${header.type_id}`).default
  } catch (e) {
    return <></>
  }

  let Footer
  try {
    Footer = require(`./components/footer/${footer.type_id}`).default
  } catch (e) {
    return <></>
  }

  // Agregá esto antes del return del wrapPageElement
  const CSSVariables = ({ global_data }: any) => {
    const {
      pageConfig: {
        colors: { primaryColor, secondaryColor, tertiaryColor, backgroundColor },
        design: {
          border1,
          border2,
          border3,
          border4,
          imageBorder1,
          imageBorder2,
          imageBorder3,
          imageBorder4,
          componentMargins0,
          mobileFontMultiplier = '0.9',
          h1,
          h2,
          h3,
          h4,
          h5,
          p,
          a,
          font_size,
          buttonPadding1 = 0,
          buttonPadding2 = 0,
          buttonUnderline = false,
          buttonItalic = false,
          global,
        },
      },
    } = global_data

    useEffect(() => {
      const root = document.documentElement
      root.style.setProperty('--primaryColor', primaryColor)
      root.style.setProperty('--secondaryColor', secondaryColor)
      root.style.setProperty('--tertiaryColor', tertiaryColor)
      root.style.setProperty('--backgroundColor', backgroundColor || '#f8f8f8')
      root.style.setProperty('--grayColor', '#6b6b6b')
      root.style.setProperty(
        '--customBorders',
        `${border1}px ${border2}px ${border3}px ${border4}px`,
      )
      root.style.setProperty(
        '--borderImages',
        `${imageBorder1}px ${imageBorder2}px ${imageBorder3}px ${imageBorder4}px`,
      )
      root.style.setProperty('--componentMargins', `${componentMargins0}vh`)
      root.style.setProperty('--mobileFontMultiplier', mobileFontMultiplier ?? '0.9')
      root.style.setProperty('--globalfontSize', `calc(var(--fontSize${font_size || 'M'}))`)
      root.style.setProperty('--border1', `${border1}px`)
      root.style.setProperty('--border2', `${border2}px`)
      root.style.setProperty('--border3', `${border3}px`)
      root.style.setProperty('--border4', `${border4}px`)
      root.style.setProperty('--paddingButton', `${buttonPadding1}rem ${buttonPadding2}rem`)
      root.style.setProperty(
        '--paddingButtonMobile',
        `${buttonPadding1 * 1.2}rem ${buttonPadding2}rem`,
      )
      root.style.setProperty('--buttonUnderline', buttonUnderline ? 'underline' : 'none')
      root.style.setProperty('--buttonItalic', buttonItalic ? 'italic' : 'none')
      root.style.setProperty('--h1LSpacing', h1?.letterSpacing ? h1.letterSpacing + 'px' : '0px')
      root.style.setProperty('--h2LSpacing', h2?.letterSpacing ? h2.letterSpacing + 'px' : '0px')
      root.style.setProperty('--h3LSpacing', h3?.letterSpacing ? h3.letterSpacing + 'px' : '0px')
      root.style.setProperty('--h4LSpacing', h4?.letterSpacing ? h4.letterSpacing + 'px' : '0px')
      root.style.setProperty('--h5LSpacing', h5?.letterSpacing ? h5.letterSpacing + 'px' : '0px')
      root.style.setProperty('--pLSpacing', p?.letterSpacing ? p.letterSpacing + 'px' : '0px')
      root.style.setProperty('--aLSpacing', a?.letterSpacing ? a.letterSpacing + 'px' : '0px')
      root.style.setProperty(
        '--globalLSpacing',
        global?.letterSpacing ? global.letterSpacing + 'px' : '0px',
      )
      root.style.setProperty('--h1Uppercase', h1?.upperCase ? 'uppercase' : 'none')
      root.style.setProperty('--h2Uppercase', h2?.upperCase ? 'uppercase' : 'none')
      root.style.setProperty('--h3Uppercase', h3?.upperCase ? 'uppercase' : 'none')
      root.style.setProperty('--h4Uppercase', h4?.upperCase ? 'uppercase' : 'none')
      root.style.setProperty('--h5Uppercase', h5?.upperCase ? 'uppercase' : 'none')
      root.style.setProperty('--pUppercase', p?.upperCase ? 'uppercase' : 'none')
      root.style.setProperty('--aUppercase', a?.upperCase ? 'uppercase' : 'none')
      root.style.setProperty('--globalUppercase', global?.upperCase ? 'uppercase' : 'none')
      root.style.setProperty('--h1Size', h1?.size && h1.size !== '0' ? `${h1.size}rem` : '3rem')
      root.style.setProperty('--h2Size', h2?.size && h2.size !== '0' ? `${h2.size}rem` : '2.48rem')
      root.style.setProperty('--h3Size', h3?.size && h3.size !== '0' ? `${h3.size}rem` : '2rem')
      root.style.setProperty('--h4Size', h4?.size && h4.size !== '0' ? `${h4.size}rem` : '1.8rem')
      root.style.setProperty('--h5Size', h5?.size && h5.size !== '0' ? `${h5.size}rem` : '1.4rem')
      root.style.setProperty('--pSize', p?.size && p.size !== '0' ? `${p.size}rem` : '1rem')
    }, []) // Solo al montar, no en cada navegación

    return null
  }

  return (
    <GlobalDataProvider global_data={global_data}>
      <CSSVariables global_data={global_data} /> {/* 👈 agregá esto */}
      <style type="text/css">{`
        @font-face {
          font-family: h1CustomFont;
          src: url(${fontMap[h1.file as keyof typeof fontMap] || h1.file});
          font-display: swap;
        }
        @font-face {
          font-family: h2CustomFont;
          src: url(${fontMap[h2.file as keyof typeof fontMap] || h2.file});
          font-display: swap;
        }
        @font-face {
          font-family: h3CustomFont;
          src: url(${fontMap[h3.file as keyof typeof fontMap] || h3.file});
          font-display: swap;
        }
        @font-face {
          font-family: h4CustomFont;
          src: url(${fontMap[h4.file as keyof typeof fontMap] || h4.file});
          font-display: swap;
        }
        @font-face {
          font-family: h5CustomFont;
          src: url(${fontMap[h5.file as keyof typeof fontMap] || h5.file});
          font-display: swap;
        }
        @font-face {
          font-family: pCustomFont;
          src: url(${fontMap[p.file as keyof typeof fontMap] || p.file});
          font-display: swap;
        }
        @font-face {
          font-family: pBoldCustomFont;
          font-weight:500;
          src: url(${fontMap[(p_bold?.file || p.file) as keyof typeof fontMap] || p_bold?.file || p.file});
          font-display: swap;
        }
        @font-face {
          font-family: aCustomFont;
          src: url(${fontMap[(a?.file || p.file) as keyof typeof fontMap] || a?.file || p.file});
          font-display: swap;
        }
        @font-face {
          font-family: globalCustomFont;
          src: url(${fontMap[global.file as keyof typeof fontMap] || global.file});
          font-display: swap;
        }
      `}</style>
      <div suppressHydrationWarning>
        <NavigationHandler />
        <Modules
          development_id={development_id}
          property_id={property_id}
          novelty_id={novelty_id}
          pages={pages}
          global_data={global_data}
        />
        {!hidden_header && (
          <Header
            development_id={development_id}
            property_id={property_id}
            novelty_id={novelty_id}
            three={three}
            {...{ type_id: header.type_id, pages, global_data, divisions, ...header.data }}
          />
        )}
        {element}
        {!hidden_footer && (
          <Footer
            {...{
              type_id: footer.type_id,
              data: footer.data,
              ...footer.data,
              pages,
              global_data,
              development_id,
              property_id,
              novelty_id,
            }}
          />
        )}
      </div>
    </GlobalDataProvider>
  )
}
